/**
 * Pins + Attention slice for the service-calls store (iter 370bq refactor).
 *
 * Owns the two parallel "elevation" features:
 *   • Pinned calls — dispatcher pins up to 3 calls to keep them visible
 *   • Attention calls — same, but a separate semantic axis ("needs my
 *     attention" vs. "pinned for follow-up")
 *
 * Plus the unscheduled-aging-goals KPI card values (oldest_goal /
 * avg_age_goal). All three live together because they're stable
 * dispatcher-controlled metadata that survives reloads.
 */
import axios from "axios";
import { toast } from "sonner";
import BACKEND_URL from "../../lib/apiConfig";

const API = `${BACKEND_URL}/api`;

export const createPinsAttentionSlice = (set, get) => ({
  pinnedCalls: [],
  attentionCalls: [],
  unscheduledGoals: { oldest_goal: 60, avg_age_goal: 30 },

  fetchPinnedCalls: async () => {
    try {
      const res = await axios.get(`${API}/service-calls/pinned`);
      set({ pinnedCalls: res.data.pinned_call_ids || [] });
    } catch (err) {
      console.error("Failed to fetch pinned calls:", err);
    }
  },

  togglePin: async (callId) => {
    const { pinnedCalls } = get();
    const isUnpinning = pinnedCalls.includes(callId);
    if (!isUnpinning && pinnedCalls.length >= 3) {
      toast.error("Max 3 attention calls. Remove one first.", { duration: 2000 });
      return;
    }
    const newPinned = isUnpinning
      ? pinnedCalls.filter(id => id !== callId)
      : [...pinnedCalls, callId];
    set({ pinnedCalls: newPinned });
    try {
      const res = await axios.post(`${API}/service-calls/pinned/toggle`, { call_id: callId });
      set({ pinnedCalls: res.data.pinned_call_ids || newPinned });
    } catch (err) {
      console.error("Failed to toggle pin:", err);
      // Revert optimistic update on failure
      set({ pinnedCalls: pinnedCalls });
    }
  },

  fetchAttentionCalls: async () => {
    try {
      const res = await axios.get(`${API}/service-calls/attention`);
      set({ attentionCalls: res.data.attention_call_ids || [] });
    } catch (err) {
      console.error("Failed to fetch attention calls:", err);
    }
  },

  toggleAttention: async (callId) => {
    const { attentionCalls } = get();
    const isAdding = !attentionCalls.includes(callId);
    if (isAdding && attentionCalls.length >= 3) {
      toast.error("Max 3 attention calls. Remove one first.");
      return;
    }
    const newAttention = isAdding
      ? [...attentionCalls, callId]
      : attentionCalls.filter(id => id !== callId);
    set({ attentionCalls: newAttention });
    try {
      const res = await axios.post(`${API}/service-calls/attention/toggle`, { call_id: callId });
      set({ attentionCalls: res.data.attention_call_ids || newAttention });
      toast.success(isAdding ? "Marked as needs attention" : "Removed from attention", { duration: 1500 });
    } catch (err) {
      console.error("Failed to toggle attention:", err);
      set({ attentionCalls: attentionCalls });
      toast.error(err.response?.data?.detail || "Failed to update");
    }
  },

  fetchUnscheduledGoals: async () => {
    try {
      const res = await axios.get(`${API}/service-calls/unscheduled-goals`);
      set({ unscheduledGoals: res.data });
    } catch (err) {
      console.error("Failed to fetch goals:", err);
    }
  },

  updateUnscheduledGoals: async (goals) => {
    try {
      const res = await axios.put(`${API}/service-calls/unscheduled-goals`, goals);
      set({ unscheduledGoals: res.data });
      toast.success("Goals updated", { duration: 1500 });
    } catch (err) {
      console.error("Failed to update goals:", err);
      toast.error("Failed to update goals");
    }
  },
});
